import React from 'react';
import { AlertCircle, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';
import type { Category, Transaction } from '@/lib/api';
import type { RowDecision, RowPair } from './useOFXReconciliation';

interface AmbiguousDecisionPanelProps {
  rows: RowPair[];
  decisions: Record<string, RowDecision>;
  categoriesById: Record<string, Category>;
  onPickManual: (fitid: string, manualId: string) => void;
  onCreateNew: (fitid: string) => void;
}

function compactDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

export const AmbiguousDecisionPanel: React.FC<AmbiguousDecisionPanelProps> = ({
  rows,
  decisions,
  categoriesById,
  onPickManual,
  onCreateNew,
}) => {
  if (rows.length === 0) return null;

  return (
    <section
      className="bg-card border border-border rounded-lg overflow-hidden animate-fade-in"
      aria-labelledby="ambiguous-title"
    >
      <header className="flex items-center gap-2 px-5 py-3 border-b border-border bg-muted/20">
        <AlertCircle className="h-4 w-4 text-foreground/70 shrink-0" aria-hidden="true" />
        <h3
          id="ambiguous-title"
          className="text-xs font-medium uppercase tracking-wide text-foreground"
        >
          Decisão necessária — {rows.length}
        </h3>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          · combinam com mais de um lançamento
        </span>
      </header>

      <ul className="divide-y divide-border/60">
        {rows.map((pair) => (
          <AmbiguousRow
            key={pair.fitid}
            pair={pair}
            decision={decisions[pair.fitid]}
            categoriesById={categoriesById}
            onPickManual={onPickManual}
            onCreateNew={onCreateNew}
          />
        ))}
      </ul>
    </section>
  );
};

interface AmbiguousRowProps {
  pair: RowPair;
  decision: RowDecision | undefined;
  categoriesById: Record<string, Category>;
  onPickManual: (fitid: string, manualId: string) => void;
  onCreateNew: (fitid: string) => void;
}

const AmbiguousRow: React.FC<AmbiguousRowProps> = ({
  pair,
  decision,
  categoriesById,
  onPickManual,
  onCreateNew,
}) => {
  const { row, fitid } = pair;
  const candidates = pair.row.matching_transactions ?? [];
  const isIncome = row.type === 'income';
  const pickedManualId =
    decision?.intent === 'link' ? decision.manualId : null;
  const isCreateNew = decision?.intent === 'create';

  return (
    <li className="px-5 py-4 animate-fade-in">
      {/* OFX line summary */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap shrink-0 w-10">
          {compactDate(row.date)}
        </span>
        <span
          className={cn(
            'inline-flex items-center justify-center h-5 w-5 rounded-full shrink-0',
            isIncome ? 'bg-income/12 text-income' : 'bg-expense/12 text-expense',
          )}
          aria-label={isIncome ? 'Receita' : 'Despesa'}
        >
          {isIncome ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        </span>
        <span className="text-sm font-medium text-foreground truncate min-w-0 flex-1">
          {row.description}
        </span>
        <span
          className={cn(
            'text-sm font-semibold tabular-nums whitespace-nowrap shrink-0',
            isIncome ? 'text-income' : 'text-expense',
          )}
        >
          {formatCurrency(row.amount)}
        </span>
      </div>

      {/* Radio group */}
      <div role="radiogroup" aria-label="Escolha o lançamento correspondente" className="space-y-1.5 pl-[3.25rem]">
        {candidates.map((candidate: Transaction) => {
          const isPicked = pickedManualId === candidate.id;
          const categoryName =
            categoriesById[candidate.category_id]?.name ?? 'Categoria desconhecida';
          return (
            <button
              key={candidate.id}
              type="button"
              role="radio"
              aria-checked={isPicked}
              onClick={() => onPickManual(fitid, candidate.id)}
              className={cn(
                'w-full text-left rounded-md border px-3 py-2 transition-colors flex items-center gap-2.5',
                isPicked
                  ? 'border-primary/50 bg-primary/5'
                  : 'border-border/60 hover:border-border hover:bg-muted/30',
              )}
            >
              <span
                className={cn(
                  'h-3.5 w-3.5 rounded-full border-2 shrink-0 transition-colors',
                  isPicked ? 'border-primary bg-primary' : 'border-muted-foreground/40',
                )}
                aria-hidden="true"
              />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-foreground truncate">
                  {candidate.description}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {categoryName} · {compactDate(candidate.date)}
                </span>
              </span>
            </button>
          );
        })}

        <button
          type="button"
          role="radio"
          aria-checked={isCreateNew}
          onClick={() => onCreateNew(fitid)}
          className={cn(
            'w-full text-left rounded-md border border-dashed px-3 py-2 transition-colors flex items-center gap-2.5',
            isCreateNew
              ? 'border-primary/50 bg-primary/5 text-foreground'
              : 'border-border/60 hover:border-border hover:bg-muted/30 text-muted-foreground',
          )}
        >
          <span
            className={cn(
              'h-3.5 w-3.5 rounded-full border-2 shrink-0 transition-colors',
              isCreateNew ? 'border-primary bg-primary' : 'border-muted-foreground/40',
            )}
            aria-hidden="true"
          />
          <span className="text-sm">Não vincular — criar nova entrada</span>
        </button>
      </div>
    </li>
  );
};
